import * as Sentry from '@sentry/vue';

import {alertCenter} from '@/alert';
import {API_LEVEL} from '@/utils/enums';
import store from '../store/store';

// Base configuration
const BASE_CONFIG = {
  baseURL: import.meta.env.VITE_API_BASE_URL,
  timeout: 120000, // Request timeout in milliseconds
  headers: {
    'Content-Type': 'application/json'
  }
};

// Request interceptor logic
const applyRequestInterceptors = (url, options) => {
  const config = {...options};

  // Add Authorization header for non-external APIs
  if (!url.startsWith('http') && store.state.authentication.accessToken) {
    config.headers = {
      ...config.headers,
      'Authorization': `Bearer ${store.state.authentication.accessToken}`
    };
  }

  // Add locale header for non-external APIs
  if (!url.startsWith('http')) {
    const lang = store.state.i18n.lang || 'en';
    config.headers = {
      ...config.headers,
      'locale': lang
    };
  }

  return config;
};

export class HttpError extends Error {
  public isHttpError: boolean;
  public status: number;
  public validateErrors: any;
  public response: any;

  constructor(message, status: number, response?: any) {
    super(message);
    this.isHttpError = true;
    this.status = status;
    this.response = response;
  }
}

export interface HttpProps {
  url: string;
  method: string;
  params?: Record<string, string | Array<string>>;
  data?: any;
  responseType?: 'arraybuffer';
  headers?: Record<string, string>;
  timeout?: number;
}

// Utility function to safely get nested properties
function getPath(obj: any, path: string | string[], defaultValue: any = undefined): any {
  const pathArray = Array.isArray(path) ? path : path.split('.');
  let current = obj;

  for (const key of pathArray) {
    if (current == null || typeof current !== 'object') {
      return defaultValue;
    }
    current = current[key];
  }

  return current == null ? defaultValue : current;
}

async function http(props: HttpProps) {
  const {url, method, params, data, responseType, headers = {}, timeout = BASE_CONFIG.timeout} = props;

  // Construct full URL
  let fullUrl = url.startsWith('http') ? url :
    url.startsWith('/') ?
      `${BASE_CONFIG.baseURL}${url}` : `${BASE_CONFIG.baseURL}/${url}`;

  // Append query params if provided
  if (params) {
    const queryParts: string[] = [];
    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach(val => {
          queryParts.push(`${key}[]=${encodeURIComponent(val)}`);
        });
      } else {
        if ('undefined' !== typeof value && value !== null) {
          queryParts.push(`${key}=${encodeURIComponent(value)}`);
        }
      }
    });
    const queryString = queryParts.join('&');
    fullUrl = queryString ? `${fullUrl}?${queryString}` : fullUrl;
  }

  // Prepare fetch options
  let options: RequestInit = {
    method: method.toUpperCase(),
    headers: {
      ...BASE_CONFIG.headers,
      ...headers
    },
    credentials: 'include' // Equivalent to withCredentials: true
  };

  // Add body for methods that support it
  if (data && ['POST', 'PUT', 'PATCH'].includes(method.toUpperCase())) {
    if (data instanceof FormData || data instanceof File) {
      // For FormData or File, set body directly and remove Content-Type (fetch sets it automatically)
      options.body = data;
      delete options.headers['Content-Type'];
    } else {
      // Assume JSON-serializable data
      options.body = JSON.stringify(data);
    }
  }

  // Apply request interceptors
  options = applyRequestInterceptors(url, options);

  // Set up AbortController for timeout
  const controller = new AbortController();
  const timeoutId = timeout > 0 ? setTimeout(() => controller.abort(), timeout) : null;

  try {
    const response = await fetch(fullUrl, {
      ...options,
      signal: controller.signal
    });

    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    if (response.status >= 400) {
      throw new HttpError(response.statusText, response.status, response);
    }

    // Handle response based on responseType
    let responseData;
    if (responseType === 'arraybuffer') {
      responseData = await response.arrayBuffer();
    } else {
      const lenStr = response.headers.get('content-length');
      if (!lenStr || parseInt(lenStr) > 0) {
        responseData = await response.json();
      } else {
        responseData = {};
      }
    }

    // Create a response object similar to axios response
    const responseObject = {
      status: response.status,
      data: responseData,
      headers: Object.fromEntries(response.headers.entries()),
      config: props
    };

    // Check API level
    if (responseObject.headers['api-level'] && responseObject.headers['api-level'] > API_LEVEL) {
      alertCenter.error('WebPage reload required.', {
        actionText: 'Reload',
        duration: 600000,
        onAction: () => {
          window.location.reload();
        }
      });
    }

    // Handle specific status codes
    const methodLower = method.toLowerCase();
    if (methodLower === 'post' && response.status === 201) {
      /*
      alertCenter.info('Created', {
        type: 'is-info',
        position: 'is-bottom-right',
        duration: 2000
      });
      */
    } else if (methodLower === 'delete' && response.status === 204) {
      /*
      alertCenter.info('Deleted', {
        type: 'is-info',
        position: 'is-bottom-right',
        duration: 2000
      });
      */
    }

    return responseObject;
  } catch (error) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    // Handle fetch-specific errors
    if (error.name === 'AbortError') {
      throw new HttpError('Request timed out', 408);
    }

    // Extract error information
    const response = error.response;
    let status = 500;

    const errorResponse = error.response || error;

    let errorMessage;
    // If error is from a fetch response
    if (errorResponse instanceof Response) {
      const response = errorResponse;
      status = response.status;
      try {
        const data = await response.json();
        errorMessage = data.message || data.stack?.message || data.error?.message;
        console.error(error, fullUrl, data)
      } catch (e) {
        console.error(e);
      }
    }

    errorMessage = errorMessage || error.message || 'Something went wrong, please try again.';

    // Handle rate limiting
    if (status === 429) {
      alertCenter.error('Rate limit exceeded');
      throw new HttpError('Rate limit exceeded', 429);
    }

    // Handle JWT expiration
    if (errorMessage === 'jwt expired' || errorMessage === 'jwt malformed') {
      try {
        const accessToken = await store.dispatch('authentication/refreshToken');
        if (accessToken) {
          props.headers = {
            ...props.headers,
            'Authorization': `Bearer ${store.state.authentication.accessToken}`
          };
          // Retry the request with new token
          return await http(props);
        }
      } catch (err) {
        Sentry.captureException(err);
        if (props) {
          Sentry.captureMessage('Session has timed out for ' + props.method + ' ' + props.url + ' ' + JSON.stringify(props.data));
        }
        alertCenter.error('Your session has timed out');
        await store.dispatch('authentication/logout', {skipCall: true});
        throw new HttpError('Your session has timed out', 490);
      }
    }

    // Handle specific status codes
    if (status === 404) {
      throw new HttpError(`${errorMessage} [${fullUrl}]`, status, response);
    }
    if (status === 401) {
      throw new HttpError(`${errorMessage} [${fullUrl}]`, status, response);
    }
    if (status === 400) {
      throw new HttpError(`${errorMessage} [${fullUrl}]`, status, response);
    }

    const throwError = new HttpError(errorMessage, 400, response);
    if (errorMessage === 'Validation error' && response?.data?.error?.data) {
      throwError.validateErrors = response.data.error.data;
    }

    throw throwError;
  }
}

export default http;
